import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { listLeads } from "@/lib/api";
import { Shield, RefreshCcw } from "lucide-react";

export default function AdminLeads() {
    const [password, setPassword] = useState("");
    const [authed, setAuthed] = useState(false);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async (pw) => {
        setLoading(true);
        try {
            const data = await listLeads(pw);
            setLeads(data);
            setAuthed(true);
        } catch {
            toast.error("Wrong password.");
            setAuthed(false);
        } finally {
            setLoading(false);
        }
    };

    if (!authed) {
        return (
            <div className="max-w-md mx-auto px-5 py-24" data-testid="admin-login">
                <div className="bg-white rounded-3xl border border-slate-200 p-8">
                    <div className="w-12 h-12 rounded-2xl bg-[#E8F8FE] flex items-center justify-center"><Shield className="w-5 h-5 text-[#1BB6F0]" /></div>
                    <h1 className="mt-4 text-2xl font-semibold text-[#1B2E7A]">Admin — Leads</h1>
                    <p className="mt-1 text-sm text-slate-600">Enter the admin password to view captured leads.</p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); load(password); }}
                        className="mt-6 space-y-3"
                    >
                        <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
                        <button
                            type="submit"
                            className="w-full bg-[#1B2E7A] hover:bg-[#13215A] text-white rounded-full px-6 py-3 font-medium transition-colors"
                            data-testid="admin-login-btn"
                            disabled={loading}
                        >
                            {loading ? "Verifying…" : "Enter"}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="admin-leads">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Admin</div>
                    <h1 className="mt-2 text-3xl font-semibold text-[#1B2E7A]">Leads ({leads.length})</h1>
                </div>
                <button
                    onClick={() => load(password)}
                    className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#1BB6F0] text-[#1B2E7A] rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
                    data-testid="admin-refresh"
                >
                    <RefreshCcw className="w-4 h-4" /> Refresh
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[10px]">
                            <tr>
                                {["Time", "Name", "Phone", "Email", "City", "Type", "Size", "Estimate", "Source"].map((h) => (
                                    <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((l) => (
                                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`lead-row-${l.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                                    <td className="px-4 py-3 font-medium text-[#1B2E7A]">{l.name}</td>
                                    <td className="px-4 py-3">{l.phone}</td>
                                    <td className="px-4 py-3">{l.email || "-"}</td>
                                    <td className="px-4 py-3">{l.city || "-"}</td>
                                    <td className="px-4 py-3">{l.property_type || "-"}</td>
                                    <td className="px-4 py-3">{l.home_size || "-"}</td>
                                    <td className="px-4 py-3 font-mono">{l.estimated_amount ? `₹${l.estimated_amount.toLocaleString("en-IN")}` : "-"}</td>
                                    <td className="px-4 py-3 text-xs text-slate-500">{l.source_page || "-"}</td>
                                </tr>
                            ))}
                            {leads.length === 0 && (
                                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No leads yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
